/**
 * Utilitários para autenticação
 */
import api from '../services/api';

/**
 * Decodifica o token JWT (sem verificar assinatura)
 * @returns {Object|null} Dados do token ou null
 */
export function decodeToken() {
    try {
        const token = localStorage.getItem('token');
        if (!token) return null;

        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Erro ao decodificar token:', error);
        return null;
    }
}

/**
 * Verifica se o usuário atual é administrador
 * @returns {boolean}
 */
export function isAdmin() {
    const decoded = decodeToken();
    return decoded?.role === 'ADMIN';
}

/**
 * Obtém o ID do usuário atual
 * @returns {number|null}
 */
export function getUserId() {
    const decoded = decodeToken();
    return decoded?.id || null;
}

/**
 * Obtém o username do usuário atual
 * @returns {string|null}
 */
export function getUsername() {
    const decoded = decodeToken();
    return decoded?.username || null;
}

/**
 * Obtém o nome do usuário atual
 * @returns {string|null}
 */
export function getUserName() {
    const decoded = decodeToken();
    return decoded?.nome || decoded?.username || null;
}

/**
 * Cache de permissões do usuário
 */
let permissionsCache = null;
let permissionsCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Carrega as permissões do usuário atual do backend
 * @returns {Promise<Object>} { isAdmin: boolean, permissions: Array }
 */
export async function loadPermissions() {
    // Verifica cache
    if (permissionsCache && permissionsCacheTime && (Date.now() - permissionsCacheTime) < CACHE_DURATION) {
        return permissionsCache;
    }

    try {
        const token = localStorage.getItem('token');
        if (!token) {
            return { isAdmin: false, permissions: [] };
        }

        // Usa a instância do axios 'api' que já adiciona o token via interceptor e trata erros
        const response = await api.get('/auth/permissoes');
        
        const data = response.data;
        permissionsCache = data;
        permissionsCacheTime = Date.now();
        return data;
    } catch (error) {
        // Se for erro 401, o interceptor já redireciona para login
        // Para outros erros, apenas loga e retorna vazio
        if (error.response?.status !== 401 && process.env.NODE_ENV === 'development') {
            console.error('Erro ao carregar permissões:', error.response?.status, error.response?.data || error.message);
        }
        return { isAdmin: false, permissions: [] };
    }
}

/**
 * Limpa o cache de permissões (útil após logout ou mudanças de permissões)
 */
export function clearPermissionsCache() {
    permissionsCache = null;
    permissionsCacheTime = null;
}

/**
 * Verifica se o usuário tem uma permissão específica
 * @param {string} recurso - Nome do recurso (ex: 'fluxo_caixa')
 * @param {string} acao - Ação (ex: 'view', 'create', 'statistics', 'reports')
 * @returns {Promise<boolean>}
 */
export async function hasPermission(recurso, acao) {
    // Admin sempre tem acesso
    if (isAdmin()) {
        return true;
    }

    const perms = await loadPermissions();
    
    if (perms.isAdmin) {
        return true;
    }

    // Verifica se tem a permissão específica
    return perms.permissions.some(p => 
        p.recurso === recurso && 
        p.acao === acao && 
        p.permitido === 1
    );
}

/**
 * Verifica se o usuário tem acesso ao Fluxo de Caixa (qualquer permissão relacionada)
 * @returns {Promise<boolean>}
 */
export async function hasFluxoCaixaAccess() {
    if (isAdmin()) {
        return true;
    }

    const perms = await loadPermissions();
    
    if (perms.isAdmin) {
        return true;
    }

    // Verifica se tem qualquer permissão relacionada ao fluxo_caixa
    return perms.permissions.some(p => 
        p.recurso === 'fluxo_caixa' && 
        p.permitido === 1
    );
}
